import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Plus, Edit, Trash2, Search, X, Download, Upload,
  Phone, Mail, Building2, Globe, FileText, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, AlertTriangle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { proveedoresAPI, tipoContactoAPI, catalogosAPI } from '../utils/api';

const TIPOS_DOCUMENTO = [
  { value: 'none', label: 'Sin documento' },
  { value: 'DNI', label: 'DNI' },
  { value: 'RUC', label: 'RUC' },
  { value: 'Pasaporte', label: 'Pasaporte' },
  { value: 'Carnet de Extranjería', label: 'Carnet de Extranjería' }
];

const Proveedores = () => {
  // Estados de datos
  const [proveedores, setProveedores] = useState([]);
  const [tiposContacto, setTiposContacto] = useState([]);
  const [categorias, setCategorias] = useState([]);
  
  // Estados de paginación
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Estados de importación
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    tipo_documento: '',
    documento_identidad: '',
    tipo_contacto_id: '',
    categoria_ids: [],
    direccion: '',
    correo: '',
    correos_secundarios: '',
    empresa: '',
    puesto: '',
    pagina_web: '',
    activo: true,
    observaciones: ''
  });

  // Cargar datos iniciales - TODO EN PARALELO
  useEffect(() => {
    loadInitialData();
  }, []);

  // Cargar proveedores cuando cambian filtros o página (excepto carga inicial)
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  useEffect(() => {
    if (initialLoadDone) {
      loadProveedores();
    }
  }, [page, searchTerm, filtroEstado, initialLoadDone]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Cargar TODO en paralelo
      const [tiposRes, categoriasRes, proveedoresRes] = await Promise.all([
        tipoContactoAPI.getAll(),
        catalogosAPI.getCategorias(),
        proveedoresAPI.getAll({ page: 1, page_size: pageSize })
      ]);
      
      setTiposContacto(tiposRes.data || []);
      
      // Ordenar categorías alfabéticamente
      const categoriasOrdenadas = (categoriasRes.data || [])
        .filter(c => c.activo)
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
      setCategorias(categoriasOrdenadas);
      
      // Cargar proveedores de la respuesta paralela
      setProveedores(proveedoresRes.data.data || []);
      setTotalPages(proveedoresRes.data.total_pages || 1);
      setTotal(proveedoresRes.data.total || 0);
      
      setInitialLoadDone(true);
    } catch (error) {
      toast.error('Error al cargar datos iniciales');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadProveedores = async () => {
    try {
      setTableLoading(true);
      const params = {
        page,
        page_size: pageSize,
        search: searchTerm || undefined,
        activo: filtroEstado === 'todos' ? undefined : filtroEstado === 'activos'
      };
      
      const response = await proveedoresAPI.getAll(params);
      setProveedores(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar proveedores');
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  };

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleOpenCreate = () => {
    setEditingProveedor(null);
    setFormData({
      nombre: '',
      telefono: '',
      tipo_documento: '',
      documento_identidad: '',
      tipo_contacto_id: '',
      categoria_ids: [],
      direccion: '',
      correo: '',
      correos_secundarios: '',
      empresa: '',
      puesto: '',
      pagina_web: '',
      activo: true,
      observaciones: ''
    });
    setShowModal(true);
  };

  const handleOpenEdit = async (proveedor) => {
    try {
      const response = await proveedoresAPI.getById(proveedor.id);
      const data = response.data;
      setEditingProveedor(data);
      setFormData({
        nombre: data.nombre || '',
        telefono: data.telefono || '',
        tipo_documento: data.tipo_documento || '',
        documento_identidad: data.documento_identidad || '',
        tipo_contacto_id: data.tipo_contacto_id || '',
        categoria_ids: data.categoria_ids || [],
        direccion: data.direccion || '',
        correo: data.correo || '',
        correos_secundarios: data.correos_secundarios || '',
        empresa: data.empresa || '',
        puesto: data.puesto || '',
        pagina_web: data.pagina_web || '',
        activo: data.activo ?? true,
        observaciones: data.observaciones || ''
      });
      setShowModal(true);
    } catch (error) {
      toast.error('Error al cargar datos del proveedor');
    }
  };

  const handleSave = async () => {
    // Validaciones
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formData.telefono.trim()) {
      toast.error('El teléfono es obligatorio');
      return;
    }
    if (!formData.correo.trim()) {
      toast.error('El correo es obligatorio');
      return;
    }
    if (!formData.tipo_contacto_id) {
      toast.error('El tipo de contacto es obligatorio');
      return;
    }
    if (formData.categoria_ids.length === 0) {
      toast.error('Debe seleccionar al menos una categoría');
      return;
    }
    if (formData.observaciones && formData.observaciones.length > 200) {
      toast.error('Las observaciones no pueden exceder 200 caracteres');
      return;
    }

    try {
      setSaving(true);
      if (editingProveedor) {
        await proveedoresAPI.update(editingProveedor.id, formData);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await proveedoresAPI.create(formData);
        toast.success('Proveedor creado exitosamente');
      }
      setShowModal(false);
      loadProveedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (proveedor) => {
    if (!window.confirm(`¿Está seguro de desactivar al proveedor "${proveedor.nombre}"?`)) {
      return;
    }
    try {
      await proveedoresAPI.delete(proveedor.id);
      toast.success('Proveedor desactivado exitosamente');
      loadProveedores();
    } catch (error) {
      toast.error('Error al desactivar proveedor');
    }
  };

  const handleToggleCategoria = (categoriaId) => {
    setFormData(prev => {
      const exists = prev.categoria_ids.includes(categoriaId);
      return {
        ...prev,
        categoria_ids: exists
          ? prev.categoria_ids.filter(id => id !== categoriaId)
          : [...prev.categoria_ids, categoriaId]
      };
    });
  };

  const handleSelectAllCategorias = () => {
    setFormData(prev => ({
      ...prev,
      categoria_ids: categorias.map(c => c.id)
    }));
  };

  const handleDeselectAllCategorias = () => {
    setFormData(prev => ({
      ...prev,
      categoria_ids: []
    }));
  };

  const handleExport = async () => {
    try {
      const response = await proveedoresAPI.exportExcel();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proveedores_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Archivo exportado exitosamente');
    } catch (error) {
      toast.error('Error al exportar proveedores');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Seleccione un archivo');
      return;
    }
    try {
      setImportLoading(true);
      const response = await proveedoresAPI.importExcel(importFile);
      setImportResult(response.data);
      if (response.data.success) {
        toast.success('Importación completada exitosamente');
        loadProveedores();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar proveedores');
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#9d8272]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0" data-testid="proveedores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Proveedores
          </h1>
          <p className="text-sm lg:text-base text-[#5A5A5A] mt-1">
            Gestiona los proveedores del sistema
          </p>
        </div>
        <Button
          onClick={handleOpenCreate}
          className="bg-[#9d8272] hover:bg-[#8a7162] text-white self-start sm:self-auto"
          data-testid="create-proveedor-btn"
        >
          <Plus size={16} className="mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      {/* Filtros y acciones */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* Búsqueda */}
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]" />
              <Input
                type="text"
                placeholder="Buscar por nombre, tipo, categoría, correo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 border-[#c5b8a2] focus:ring-[#9d8272] focus:border-[#9d8272]"
                data-testid="search-proveedores-input"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#2D2D2D]"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Filtro Estado */}
            <Select value={filtroEstado} onValueChange={(value) => { setFiltroEstado(value); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40 border-[#c5b8a2]" data-testid="filter-estado">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="activos">Activos</SelectItem>
                <SelectItem value="inactivos">Inactivos</SelectItem>
              </SelectContent>
            </Select>

            {/* Botones Export/Import */}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={handleExport}
                className="flex-1 sm:flex-none border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white"
                data-testid="export-proveedores-btn"
              >
                <Download size={16} className="mr-2" />
                Exportar
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowImportModal(true)}
                className="flex-1 sm:flex-none border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white"
                data-testid="import-proveedores-btn"
              >
                <Upload size={16} className="mr-2" />
                Importar
              </Button>
            </div>
          </div>

          {/* Contador */}
          <div className="mt-3 text-sm text-[#888888]">
            Mostrando {proveedores.length} de {total} proveedores
          </div>
        </CardContent>
      </Card>

      {/* Tabla de proveedores */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm overflow-hidden relative">
        {tableLoading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
            <Loader2 className="h-6 w-6 animate-spin text-[#9d8272]" />
          </div>
        )}
        
        {/* Vista Desktop */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#FAFAFA] border-b border-[#E8E8E7]">
              <tr>
                <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Nombre</th>
                <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Teléfono</th>
                <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Tipo Contacto</th>
                <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Categorías</th>
                <th className="text-left p-3 text-sm font-semibold text-[#2D2D2D]">Correo</th>
                <th className="text-center p-3 text-sm font-semibold text-[#2D2D2D]">Estado</th>
                <th className="text-center p-3 text-sm font-semibold text-[#2D2D2D]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {proveedores.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-[#888888]">
                    {searchTerm ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
                  </td>
                </tr>
              ) : (
                proveedores.map((proveedor) => (
                  <tr key={proveedor.id} className="border-b border-[#E8E8E7] hover:bg-[#FAFAFA]">
                    <td className="p-3">
                      <div className="font-medium text-[#2D2D2D]">{proveedor.nombre}</div>
                      {proveedor.empresa && (
                        <div className="text-xs text-[#888888]">{proveedor.empresa}</div>
                      )}
                    </td>
                    <td className="p-3 text-sm text-[#5A5A5A]">{proveedor.telefono}</td>
                    <td className="p-3">
                      <Badge variant="outline" className="bg-[#f5f0eb] text-[#9d8272] border-[#c5b8a2]">
                        {proveedor.tipo_contacto_nombre}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(proveedor.categorias || []).slice(0, 2).map((cat) => (
                          <Badge key={cat.id} variant="secondary" className="text-xs bg-[#f0ebe6] text-[#6b5b4f] border border-[#d4c4b5]">
                            {cat.nombre}
                          </Badge>
                        ))}
                        {(proveedor.categorias || []).length > 2 && (
                          <Badge variant="secondary" className="text-xs bg-[#9d8272] text-white">
                            +{proveedor.categorias.length - 2}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-sm text-[#5A5A5A]">{proveedor.correo}</td>
                    <td className="p-3 text-center">
                      {proveedor.activo ? (
                        <Badge className="bg-green-100 text-green-700">Activo</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Inactivo</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(proveedor)}
                          className="text-[#9d8272] hover:text-[#8a7162] hover:bg-[#f5f0eb]"
                        >
                          <Edit size={16} />
                        </Button>
                        {proveedor.activo && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(proveedor)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Vista Mobile/Tablet - Cards */}
        <div className="lg:hidden">
          {proveedores.length === 0 ? (
            <div className="text-center py-8 text-[#888888]">
              {searchTerm ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
            </div>
          ) : (
            <div className="divide-y divide-[#E8E8E7]">
              {proveedores.map((proveedor) => (
                <div key={proveedor.id} className="p-4 hover:bg-[#FAFAFA]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-[#2D2D2D]">{proveedor.nombre}</h3>
                        {proveedor.activo ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">Activo</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-xs">Inactivo</Badge>
                        )}
                      </div>
                      {proveedor.empresa && (
                        <p className="text-sm text-[#888888] mt-0.5">{proveedor.empresa}</p>
                      )}
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                          <Phone size={14} />
                          {proveedor.telefono}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                          <Mail size={14} />
                          {proveedor.correo}
                        </div>
                      </div>
                      <div className="mt-2">
                        <Badge variant="outline" className="bg-[#f5f0eb] text-[#9d8272] border-[#c5b8a2] text-xs">
                          {proveedor.tipo_contacto_nombre}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(proveedor.categorias || []).slice(0, 3).map((cat) => (
                          <Badge key={cat.id} variant="secondary" className="text-xs bg-[#f0ebe6] text-[#6b5b4f] border border-[#d4c4b5]">
                            {cat.nombre}
                          </Badge>
                        ))}
                        {(proveedor.categorias || []).length > 3 && (
                          <Badge variant="secondary" className="text-xs bg-[#9d8272] text-white">
                            +{proveedor.categorias.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(proveedor)}
                        className="text-[#9d8272]"
                      >
                        <Edit size={16} />
                      </Button>
                      {proveedor.activo && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(proveedor)}
                          className="text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border-[#c5b8a2]"
          >
            <ChevronLeft size={16} />
            <span className="hidden sm:inline ml-1">Anterior</span>
          </Button>
          <span className="text-sm text-[#5A5A5A] px-3">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border-[#c5b8a2]"
          >
            <span className="hidden sm:inline mr-1">Siguiente</span>
            <ChevronRight size={16} />
          </Button>
        </div>
      )}

      {/* Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingProveedor ? <Edit size={20} /> : <Plus size={20} />}
              {editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Información Básica */}
            <div>
              <h3 className="text-sm font-semibold text-[#2D2D2D] mb-3 flex items-center gap-2">
                <Users size={16} />
                Información Básica
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Nombre *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Nombre completo"
                    className="border-[#c5b8a2]"
                    data-testid="input-nombre"
                  />
                </div>
                <div>
                  <Label className="text-sm">Teléfono *</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                    placeholder="999 888 777"
                    className="border-[#c5b8a2]"
                    data-testid="input-telefono"
                  />
                </div>
                <div>
                  <Label className="text-sm">Tipo de Documento</Label>
                  <Select 
                    value={formData.tipo_documento || 'none'} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_documento: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger className="border-[#c5b8a2]">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_DOCUMENTO.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Documento de Identidad</Label>
                  <Input
                    value={formData.documento_identidad}
                    onChange={(e) => setFormData(prev => ({ ...prev, documento_identidad: e.target.value.slice(0, 20) }))}
                    placeholder="Número de documento"
                    maxLength={20}
                    className="border-[#c5b8a2]"
                  />
                </div>
                <div>
                  <Label className="text-sm">Tipo de Contacto *</Label>
                  <Select 
                    value={formData.tipo_contacto_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_contacto_id: value }))}
                  >
                    <SelectTrigger className="border-[#c5b8a2]" data-testid="select-tipo-contacto">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposContacto.map(tipo => (
                        <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Correo *</Label>
                  <Input
                    type="email"
                    value={formData.correo}
                    onChange={(e) => setFormData(prev => ({ ...prev, correo: e.target.value }))}
                    placeholder="correo@ejemplo.com"
                    className="border-[#c5b8a2]"
                    data-testid="input-correo"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Correos Secundarios</Label>
                  <Input
                    type="text"
                    value={formData.correos_secundarios}
                    onChange={(e) => setFormData(prev => ({ ...prev, correos_secundarios: e.target.value }))}
                    placeholder="correo1@ejemplo.com; correo2@ejemplo.com"
                    className="border-[#c5b8a2]"
                    data-testid="input-correos-secundarios"
                  />
                  <p className="text-xs text-[#888888] mt-1">
                    Separar múltiples correos con punto y coma (;)
                  </p>
                </div>
              </div>
            </div>

            {/* Categorías */}
            <div>
              <h3 className="text-sm font-semibold text-[#2D2D2D] mb-3 flex items-center gap-2">
                <FileText size={16} />
                Categorías *
              </h3>
              <div className="border border-[#E8E8E7] rounded-lg p-3">
                <div className="flex gap-2 mb-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllCategorias}
                    className="text-xs border-[#9d8272] text-[#9d8272]"
                  >
                    Seleccionar todas
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAllCategorias}
                    className="text-xs border-gray-300"
                  >
                    Deseleccionar todas
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {categorias.map(categoria => (
                    <label
                      key={categoria.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#FAFAFA] p-1 rounded"
                    >
                      <Checkbox
                        checked={formData.categoria_ids.includes(categoria.id)}
                        onCheckedChange={() => handleToggleCategoria(categoria.id)}
                      />
                      <span className="truncate">{categoria.nombre}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-xs text-[#888888]">
                  {formData.categoria_ids.length} categoría(s) seleccionada(s)
                </div>
              </div>
            </div>

            {/* Información de Empresa */}
            <div>
              <h3 className="text-sm font-semibold text-[#2D2D2D] mb-3 flex items-center gap-2">
                <Building2 size={16} />
                Información de Empresa
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Empresa</Label>
                  <Input
                    value={formData.empresa}
                    onChange={(e) => setFormData(prev => ({ ...prev, empresa: e.target.value }))}
                    placeholder="Razón social"
                    className="border-[#c5b8a2]"
                  />
                </div>
                <div>
                  <Label className="text-sm">Puesto</Label>
                  <Input
                    value={formData.puesto}
                    onChange={(e) => setFormData(prev => ({ ...prev, puesto: e.target.value }))}
                    placeholder="Cargo en la empresa"
                    className="border-[#c5b8a2]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm">Dirección</Label>
                  <Input
                    value={formData.direccion}
                    onChange={(e) => setFormData(prev => ({ ...prev, direccion: e.target.value }))}
                    placeholder="Dirección fiscal"
                    className="border-[#c5b8a2]"
                  />
                </div>
                <div>
                  <Label className="text-sm">Página Web</Label>
                  <Input
                    value={formData.pagina_web}
                    onChange={(e) => setFormData(prev => ({ ...prev, pagina_web: e.target.value }))}
                    placeholder="https://www.ejemplo.com"
                    className="border-[#c5b8a2]"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Checkbox
                    id="activo"
                    checked={formData.activo}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, activo: checked }))}
                  />
                  <Label htmlFor="activo" className="text-sm cursor-pointer">
                    Proveedor Activo
                  </Label>
                </div>
              </div>
            </div>

            {/* Observaciones */}
            <div>
              <Label className="text-sm">Observaciones</Label>
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value.slice(0, 200) }))}
                placeholder="Observaciones adicionales..."
                rows={3}
                className="w-full border border-[#c5b8a2] rounded-md p-2 text-sm focus:ring-[#9d8272] focus:border-[#9d8272]"
                maxLength={200}
              />
              <div className="text-xs text-[#888888] text-right mt-1">
                {formData.observaciones.length}/200
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Importar */}
      <Dialog open={showImportModal} onOpenChange={closeImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} />
              Importar Proveedores
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!importResult ? (
              <>
                <div>
                  <Label className="text-sm">Seleccionar archivo Excel (.xlsx)</Label>
                  <Input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="border-[#c5b8a2] mt-1"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Instrucciones:</p>
                      <ul className="list-disc ml-4 mt-1 space-y-1 text-xs">
                        <li>Exporte primero para obtener la plantilla</li>
                        <li>Si el correo existe, se actualizará el proveedor</li>
                        <li>Si el correo no existe, se creará un nuevo proveedor</li>
                        <li>En Categorías: use "TODAS" para asignar todas, o separe con ";"</li>
                        <li>Si hay errores, no se realizará ningún cambio</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={closeImportModal}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={!importFile || importLoading}
                    className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                  >
                    {importLoading ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Importar
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {importResult.success ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-700 font-medium mb-3">
                      <CheckCircle size={20} />
                      Importación Exitosa
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="bg-white rounded-lg p-3">
                        <div className="text-2xl font-bold text-green-600">
                          {importResult.estadisticas.insertados}
                        </div>
                        <div className="text-xs text-[#888888]">Insertados</div>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <div className="text-2xl font-bold text-blue-600">
                          {importResult.estadisticas.actualizados}
                        </div>
                        <div className="text-xs text-[#888888]">Actualizados</div>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <div className="text-2xl font-bold text-[#2D2D2D]">
                          {importResult.estadisticas.total_procesados}
                        </div>
                        <div className="text-xs text-[#888888]">Total</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700 font-medium mb-3">
                      <XCircle size={20} />
                      Errores Encontrados
                    </div>
                    <p className="text-sm text-red-600 mb-3">
                      No se realizó ningún cambio. Corrija los errores e intente nuevamente.
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {importResult.errores.map((error, idx) => (
                        <div key={idx} className="bg-white rounded p-2 text-sm">
                          <span className="font-medium">Fila {error.fila}:</span>{' '}
                          <span className="text-[#888888]">{error.campo}</span>{' '}
                          {error.valor && <span className="text-red-500">"{error.valor}"</span>}{' '}
                          - {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={closeImportModal} className="bg-[#9d8272] hover:bg-[#8a7162] text-white">
                    Cerrar
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Proveedores;
